import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { FormWrapper } from '@/form-control'
import { PasswordField, TextField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'

import { AuthToken } from '@/MODELS/types'

import { ApiResponse, api_no_auth, setAccessToken, tryRefreshAccessToken } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import AuthLayout from './_Layout'

interface ICredentials {
    email: string
    password: string
}

interface LoginResponse {
    token?: AuthToken
    redirectUrl?: string
    requiresOtp?: boolean
    otpSessionId?: string
}

export default function LoginPage() {
    const { fetchUser, user, state } = useAuth()
    const navigate = useNavigate()

    const [showResumeModal, setShowResumeModal] = useState(false)

    // Set when the submitted email belongs to a coordinator account — the
    // notice links them to the provider portal instead of a plain error.
    const [coordinatorPortalUrl, setCoordinatorPortalUrl] = useState<string | null>(null)

    // If user is authenticated, redirect. If mid-onboarding, show resume modal.
    // Only check sessionStorage once auth has resolved to "unauthenticated" — otherwise
    // we can flash the modal during the initial auth-checking window for a logged-in user.
    useEffect(() => {
        if (user) {
            if (!user.onboardingComplete) {
                navigate('/onboarding', { replace: true })
            } else {
                navigate('/', { replace: true })
            }
        } else if (state === 'unauthenticated' && sessionStorage.getItem('brightpath-onboarding')) {
            setShowResumeModal(true)
        }
    }, [user, state, navigate])

    // Handle Safari bfcache — page restored from back button doesn't re-mount React
    useEffect(() => {
        const handler = (e: PageTransitionEvent) => {
            if (e.persisted && sessionStorage.getItem('brightpath-onboarding')) {
                setShowResumeModal(true)
            }
        }
        window.addEventListener('pageshow', handler)
        return () => window.removeEventListener('pageshow', handler)
    }, [])

    const [otpState, setOtpState] = useState<{ otpSessionId: string } | null>(null)
    const [otpCode, setOtpCode] = useState('')
    const [otpError, setOtpError] = useState<string | null>(null)
    const [otpSubmitting, setOtpSubmitting] = useState(false)

    async function handleOtpSubmit(e: React.FormEvent) {
        e.preventDefault()
        setOtpError(null)
        setOtpSubmitting(true)
        try {
            const response = await api_no_auth<LoginResponse>(ROUTES.auth.verifyOtp, {
                otpSessionId: otpState.otpSessionId,
                code: otpCode,
            })
            if (response.error || !response.data?.token) {
                setOtpError(response.error ?? 'Verification failed')
                return
            }
            setAccessToken(response.data.token)
            await fetchUser()
            navigate(response.data.redirectUrl ?? '/', { replace: true })
        } catch (err: any) {
            setOtpError(err?.error ?? 'Verification failed. Please try again.')
        } finally {
            setOtpSubmitting(false)
        }
    }

    if (otpState) {
        return (
            <AuthLayout info={<></>}>
                <h1 className='mb-3 text-3xl font-medium text-center'>Verify Your Identity</h1>
                <p className='mb-6 text-center text-neutral-500'>
                    Enter the 6-digit code sent to your phone.
                </p>
                <form onSubmit={handleOtpSubmit} className='w-full space-y-4'>
                    <div>
                        <input
                            type='text'
                            inputMode='numeric'
                            pattern='[0-9]{6}'
                            maxLength={6}
                            autoComplete='one-time-code'
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            autoFocus
                            placeholder='000000'
                            className='w-full px-4 py-3 text-2xl font-mono tracking-[0.5em] text-center border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                        />
                    </div>

                    {otpError && (
                        <div className='p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg'>
                            {otpError}
                        </div>
                    )}

                    <button
                        type='submit'
                        disabled={otpCode.length !== 6 || otpSubmitting}
                        className='w-full py-3 text-white rounded-lg bg-brand-500 hover:bg-brand-400 disabled:opacity-50 disabled:cursor-not-allowed font-medium'
                    >
                        {otpSubmitting ? 'Verifying...' : 'Verify'}
                    </button>

                    <button
                        type='button'
                        onClick={() => {
                            setOtpState(null)
                            setOtpCode('')
                            setOtpError(null)
                        }}
                        className='w-full py-2 text-sm font-medium text-brand-400 hover:text-brand-600'
                    >
                        Back to login
                    </button>
                </form>
            </AuthLayout>
        )
    }

    return (
        <>
        {showResumeModal && (
            <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60'>
                <div className='w-full max-w-sm mx-4 p-6 bg-white rounded-2xl shadow-xl'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>Resume setup?</h3>
                    <p className='text-sm text-gray-600 mb-5'>
                        It looks like you were in the middle of setting up your account. Would you like to pick up where you left off?
                    </p>
                    <div className='flex gap-3'>
                        <button
                            onClick={() => {
                                sessionStorage.removeItem('brightpath-onboarding')
                                setShowResumeModal(false)
                            }}
                            className='flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50'
                        >
                            Start over
                        </button>
                        <button
                            onClick={async () => {
                                const refreshed = await tryRefreshAccessToken()
                                if (refreshed) {
                                    navigate('/onboarding', { replace: true })
                                } else {
                                    // Session expired — need to log in first
                                    setShowResumeModal(false)
                                }
                            }}
                            className='flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-400'
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        )}
        <AuthLayout info={<></>}>
            <h1 className='mb-3 text-3xl font-medium text-center'>Sign In</h1>
            <h2 className='mb-5 text-center text-neutral-500'>
                Don't have an account?{' '}
                <a className='text-brand-400 hover:text-brand-600' href='/register'>
                    Sign Up
                </a>
            </h2>
            {coordinatorPortalUrl && (
                <div className='w-full mb-4 p-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg'>
                    This email belongs to a coordinator account.{' '}
                    <a href={`${coordinatorPortalUrl}/login`} className='font-medium underline'>
                        Sign in at the provider portal
                    </a>
                    .
                </div>
            )}
            <FormWrapper<ICredentials>
                endpoint={ROUTES.auth.login}
                id={null}
                isPublic
                preventInitialLoad
                onSubmit={async (formValues) => {
                    setCoordinatorPortalUrl(null)
                    // Submitted directly (not via FormWrapper's internal post)
                    // so the structured cross-portal rejection can be handled
                    // in place: suppressForbiddenRedirect keeps the 403 from
                    // navigating to /forbidden.
                    const response: ApiResponse<LoginResponse> = await api_no_auth<LoginResponse>(
                        ROUTES.auth.login,
                        { email: formValues?.email, password: formValues?.password },
                        undefined,
                        undefined,
                        true
                    )

                    if (response.error) {
                        const errData = response.data as { code?: string; portalUrl?: string } | undefined
                        if (errData?.code === 'COORDINATOR_ACCOUNT' && errData.portalUrl) {
                            setCoordinatorPortalUrl(errData.portalUrl)
                            return
                        }
                        throw { error: response.error }
                    }

                    const data = response.data
                    if (data?.requiresOtp && data?.otpSessionId) {
                        setOtpState({ otpSessionId: data.otpSessionId })
                        return
                    }

                    if (!data?.token) {
                        throw {
                            error: 'Login unsuccessful, please check your details and try again.',
                        }
                    }

                    setAccessToken(data.token)
                    await fetchUser()

                    navigate(data.redirectUrl ?? '/', { replace: true })
                }}
                submitButtonText='Login'
                submitButtonAlignment='center'
                submitButtonClass={'bg-brand-500  hover:bg-brand-400'}
                //
                validationRules={[
                    {
                        field: 'email',
                        isValid: (v) => (v ? true : false),
                        prompt: 'Please provide your email address',
                    },
                    {
                        field: 'password',
                        isValid: (v) => (v ? true : false),
                        prompt: 'Please provide your password',
                    },
                ]}
                //
                className='w-full'
            >
                {(f) => (
                    <>
                        <TextField {...f('email')} autoComplete='email' type='email' name='email' />
                        <PasswordField {...f('password')} autoComplete='current-password' name='password' />

                        <div className='flex justify-start items-center'>
                            <a href='/request-reset' className='font-medium text-brand-400 hover:text-brand-600'>
                                Forgot your password?
                            </a>
                        </div>
                    </>
                )}
            </FormWrapper>
        </AuthLayout>
        </>
    )
}
